import { useMemo, useCallback } from 'react';

import CandidateDetail from 'src/sections/candidates/view/candidate-detail-view';
import ContactsDetailView from 'src/sections/contacts/view/contacts-detail-view';
import MyCompaniesDetailView from 'src/sections/companies/detail/companies-detail-view';

import { useRecordPanel } from './use-record-panel';
import RecordPanelFrame from './record-panel-frame';
import { RecordPanelType } from './record-panel.types';
import ContactRecordView from './views/contact-record-view';
import CompanyRecordView from './views/company-record-view';
import { resolveRecordNavigation } from './record-panel.utils';
import CandidateRecordView from './views/candidate-record-view';
import ApplicationRecordView from './views/application-record-view';

// ----------------------------------------------------------------------

interface RecordPanelProps {
  /** The entity type this surface owns; the panel opens only for a matching `?preview_type`. */
  type: RecordPanelType;
  /** Ordered ids of the current result set (the loaded rows) — drives prev/next and "Result N of M". */
  ids: string[];
  /** Total across all pages (defaults to ids.length) so the position reads N of <total>, not N of <loaded>. */
  total?: number;
  /**
   * Whether this panel claims a cold deep-link that omits `?preview_type`.
   *
   * Defaults to true, which is right when a surface renders a single panel. A surface rendering TWO
   * panels (the applications list opens applications AND candidates) must set it false on one of
   * them, or an untyped `?preview` would open both at once.
   */
  fallback?: boolean;
}

/**
 * Co-located record panel (JT1-1529). Each list surface renders one, typed to its own entity, and
 * passes the loaded row ids. It reads the open record from the URL (`?preview` / `?preview_type`) and
 * walks prev/next through the in-memory ids without refetching the page. On a cold deep-link the id
 * may not be in the loaded set yet — prev/next and the position indicator simply stay hidden until the
 * list's rows arrive; the view still fetches the record by id, so the panel opens either way.
 */
export default function RecordPanel({ type, ids, total, fallback = true }: RecordPanelProps) {
  const { previewId, previewType, previewMode, openRecord, closeRecord } = useRecordPanel();

  // Open only when this surface owns the previewed type. previewType is always set on open; a cold
  // deep-link that omits it falls back to the panel that claims it (see `fallback`).
  const isOpen = Boolean(previewId) && (previewType === type || (!previewType && fallback));

  const { prevId, nextId, position } = useMemo(
    () => resolveRecordNavigation(ids, previewId, total),
    [ids, previewId, total]
  );

  const onPrev = useCallback(() => { if (prevId) openRecord(type, prevId, { replace: true }); }, [prevId, openRecord, type]);
  const onNext = useCallback(() => { if (nextId) openRecord(type, nextId, { replace: true }); }, [nextId, openRecord, type]);

  // Quick mode renders the compact per-entity RecordView; overlay mode renders the full detail view.
  const renderBody = () => {
    if (!isOpen || !previewId) return null;
    if (type === 'candidate') return previewMode === 'overlay' ? <CandidateDetail overrideCandidateId={previewId} /> : <CandidateRecordView id={previewId} />;
    if (type === 'contact') return previewMode === 'overlay' ? <ContactsDetailView overrideContactId={previewId} /> : <ContactRecordView id={previewId} />;
    if (type === 'company') return previewMode === 'overlay' ? <MyCompaniesDetailView overrideConnectionId={previewId} /> : <CompanyRecordView id={previewId} />;
    // Applications have no standalone detail route, so both modes render the same view — the frame
    // still differs (centred overlay vs right drawer).
    if (type === 'application') return <ApplicationRecordView id={previewId} />;
    return null;
  };

  return (
    <RecordPanelFrame
      variant={previewMode}
      open={isOpen}
      onClose={closeRecord}
      onPrev={prevId ? onPrev : undefined}
      onNext={nextId ? onNext : undefined}
      position={position}
    >
      {renderBody()}
    </RecordPanelFrame>
  );
}
